import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import LinkIcon from '@mui/icons-material/Link'
import API from '../../services/API'
import PageHeader from '../kit/PageHeader'
import ConfirmDialog from '../kit/ConfirmDialog'
import StatTile from '../kit/StatTile'
import dataGridSx from '../kit/dataGridSx'
import { SoftChip } from '../kit/Chips'

// Which of each organisation's own mandatory training courses are linked to
// the e-learning catalogue. A course is matched by its title; where exactly
// one matches it can be linked here (one at a time, or all at once), and
// where several match, a person chooses. Linking retires that course's
// results from the old e-learning, because staff can now take the new one.

const STATE = {
  linked: { label: 'Linked', color: '#00964C' },
  match: { label: 'Link', color: '#268EBB' },
  ambiguous: { label: 'Choose', color: '#B45309' },
  none: { label: 'No match', color: '#94A3B8' },
}

const errorOf = (res, fallback) => res.data?.error ?? res.data?.message ?? fallback

const OrganisationLinks = () => {
  const [data, setData] = useState({ courses: [], tenants: [] })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null)
  // { tenant, entry } while confirming one link; true while confirming all.
  const [linking, setLinking] = useState(null)
  const [linkingAll, setLinkingAll] = useState(false)
  const [chosen, setChosen] = useState('')
  const [busy, setBusy] = useState(false)
  const [dialogError, setDialogError] = useState(null)

  const load = () =>
    API.get('/admincrm/training/links')
      .then((res) => {
        if (res.status === 200) {
          setData(res.data)
          setError(null)
        } else setError(errorOf(res, 'Unable to load the organisations'))
      })
      .finally(() => setLoading(false))

  useEffect(() => {
    load()
  }, [])

  const totals = useMemo(() => {
    const count = { linked: 0, match: 0, ambiguous: 0, none: 0 }
    data.tenants.forEach((tenant) =>
      (tenant.courses ?? []).forEach((entry) => {
        count[entry.state] += 1
      }),
    )
    return count
  }, [data])

  const open = (tenant, entry) => {
    setChosen(entry.matches.length === 1 ? String(entry.matches[0]._id) : '')
    setDialogError(null)
    setLinking({ tenant, entry })
  }

  const confirmOne = async () => {
    setBusy(true)
    setDialogError(null)
    const { tenant, entry } = linking
    const res = await API.post('/admincrm/training/links', {
      tenantId: tenant.tenantId,
      code: entry.code,
      courseId: chosen,
    })
    setBusy(false)
    if (res.status !== 200) return setDialogError(errorOf(res, 'Unable to link this course'))
    setNotice(
      `${tenant.name}: "${res.data.courseTitle}" is now linked to ${entry.code}.` +
        (res.data.retired ? ` ${res.data.retired} old e-learning result(s) no longer count.` : ''),
    )
    setLinking(null)
    return load()
  }

  const confirmAll = async () => {
    setBusy(true)
    setDialogError(null)
    const res = await API.post('/admincrm/training/links/all')
    setBusy(false)
    if (res.status !== 200) return setDialogError(errorOf(res, 'Unable to link the courses'))
    const { linked, retired, ambiguous, failed } = res.data
    setNotice(
      `Linked ${linked} course(s). ${retired} old e-learning result(s) no longer count.` +
        (ambiguous ? ` ${ambiguous} still need a choice.` : '') +
        (failed ? ` ${failed} could not be linked.` : ''),
    )
    setLinkingAll(false)
    return load()
  }

  const columns = [
    {
      field: 'name',
      headerName: 'Organisation',
      minWidth: 220,
      flex: 1,
      renderCell: ({ row }) => (
        <Stack justifyContent="center" height="100%" minWidth={0}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {row.name}
            {row.isDemo ? ' (demo)' : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {row.error ?? (row.eLearning ? row.subdomain : `${row.subdomain} · no e-learning on this plan`)}
          </Typography>
        </Stack>
      ),
    },
    ...data.courses.map((course) => ({
      field: course.code,
      headerName: course.code,
      description: course.title,
      width: 104,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      valueGetter: (value, row) => row.courses?.find((entry) => entry.code === course.code)?.state ?? '',
      renderCell: ({ row }) => {
        const entry = row.courses?.find((candidate) => candidate.code === course.code)
        if (!entry) return ''
        const meta = STATE[entry.state]
        const tip =
          entry.state === 'linked'
            ? `Linked to "${entry.linkedCourse.title}"`
            : entry.state === 'match'
              ? `Their course "${entry.matches[0].title}" matches. Select to link it.`
              : entry.state === 'ambiguous'
                ? `${entry.matches.length} of their courses match. Select to choose one.`
                : 'None of their courses has a matching title. They can add the course in Site Configuration.'
        const actionable = entry.state === 'match' || entry.state === 'ambiguous'
        return (
          <Tooltip title={`${course.title}: ${tip}`}>
            <span>
              <SoftChip
                label={meta.label}
                color={meta.color}
                icon={entry.state === 'match' ? <LinkIcon /> : undefined}
                onClick={actionable ? () => open(row, entry) : undefined}
              />
            </span>
          </Tooltip>
        )
      },
    })),
  ]

  return (
    <Stack gap={2}>
      <PageHeader
        eyebrow="Training"
        title="Organisation links"
        subtitle="Which of each organisation's own mandatory training courses are linked to an e-learning course. Linking a course lets its staff take the e-learning, and its results from the old e-learning stop counting."
        actions={
          <Button
            variant="contained"
            startIcon={<LinkIcon />}
            disabled={!totals.match}
            onClick={() => {
              setDialogError(null)
              setLinkingAll(true)
            }}
          >
            Link all {totals.match} matches
          </Button>
        }
      />
      {error && <Alert severity="error">{error}</Alert>}
      {notice && (
        <Alert severity="success" onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
        <StatTile label="Linked" value={totals.linked} />
        <StatTile label="Ready to link" value={totals.match} />
        <StatTile label="Need a choice" value={totals.ambiguous} />
        <StatTile label="No matching course" value={totals.none} />
      </Stack>
      <DataGrid
        rows={data.tenants}
        columns={columns}
        loading={loading}
        getRowId={(row) => row.tenantId}
        disableRowSelectionOnClick
        autoHeight
        // Few rows and columns: render them all, so none is unreachable off screen.
        disableVirtualization
        getRowHeight={() => 56}
        sx={{ ...dataGridSx, '& .MuiDataGrid-row:hover': { backgroundColor: '#F8FAFC' } }}
        localeText={{ noRowsLabel: 'No active organisations.' }}
      />

      <ConfirmDialog
        open={!!linking}
        title={linking ? `Link ${linking.entry.code} for ${linking.tenant.name}?` : ''}
        confirmLabel="Link course"
        busy={busy || !chosen}
        error={dialogError}
        onConfirm={confirmOne}
        onClose={() => setLinking(null)}
      >
        {linking && (
          <>
            <Typography variant="body2">
              {linking.entry.matches.length === 1
                ? `Their course "${linking.entry.matches[0].title}" will be linked to ${linking.entry.code} ${linking.entry.title}.`
                : `More than one of their courses matches ${linking.entry.code} ${linking.entry.title}. Choose the one to link.`}
            </Typography>
            {linking.entry.matches.length > 1 && (
              <FormControl>
                <RadioGroup value={chosen} onChange={(event) => setChosen(event.target.value)}>
                  {linking.entry.matches.map((match) => (
                    <FormControlLabel key={match._id} value={String(match._id)} control={<Radio />} label={match.title} />
                  ))}
                </RadioGroup>
              </FormControl>
            )}
            <Alert severity="info">
              Staff whose role needs this course will be able to take the e-learning. Any results they hold from the old
              e-learning for it will stop counting, so they may show as not completed until they take the new course.
            </Alert>
          </>
        )}
      </ConfirmDialog>

      <ConfirmDialog
        open={linkingAll}
        title={`Link all ${totals.match} matches?`}
        confirmLabel="Link all"
        busy={busy}
        error={dialogError}
        onConfirm={confirmAll}
        onClose={() => setLinkingAll(false)}
      >
        <Typography variant="body2">
          Every course marked "Link" is linked, in every organisation. Courses marked "Choose" or "No match" are left
          alone.
        </Typography>
        <Alert severity="info">
          Results from the old e-learning for the linked courses stop counting, so some staff may show as not completed
          until they take the new course.
        </Alert>
      </ConfirmDialog>
    </Stack>
  )
}

export default OrganisationLinks
